import React, { useRef, useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const CHANNEL_ICONS = {
    slack: '#',
    whatsapp: '\u{1F4AC}',
    imessage: '\u{1F4F1}',
    email: '\u{2709}',
    twitter: '\u{1D54F}',
    linkedin: 'in',
};

function Avatar({ name, image_url, color }) {
    if (image_url) return <img src={image_url} alt={name} className="w-10 h-10 rounded-full object-cover" />;
    const initial = (name || '?')[0].toUpperCase();
    return (
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: color }}>
            {initial}
        </div>
    );
}

function ChannelBadge({ channel, s }) {
    const colors = {
        slack: '#4A154B', whatsapp: '#25D366', imessage: '#007AFF',
        email: '#EA4335', twitter: '#1DA1F2', linkedin: '#0A66C2',
    };
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ background: colors[channel] || s.accent }}>
            <span>{CHANNEL_ICONS[channel] || ''}</span> {channel}
        </span>
    );
}

function ReceiptCard({ item, s, index, visible }) {
    const { quote, name, title, company, channel = 'slack', image_url } = item;

    const wrapperBase = "rounded-xl overflow-hidden transition-all duration-500 ease-out";
    const stagger = { transitionDelay: visible ? `${index * 100}ms` : '0ms', opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' };

    const inner = (
        <div className="p-3 pt-2">
            <div className="flex items-center gap-2 mt-3">
                <Avatar name={name} image_url={image_url} color={s.accent} />
                <div>
                    <div className="font-semibold text-sm" style={{ color: s.text }}>{name}</div>
                    {(title || company) && <div className="text-xs" style={{ color: s.textSub }}>{[title, company].filter(Boolean).join(' @ ')}</div>}
                </div>
            </div>
        </div>
    );

    if (channel === 'slack') {
        return (
            <div className={wrapperBase} style={{ ...stagger, background: '#F8F8F8', borderLeft: '4px solid #4A154B' }}>
                <div className="px-4 py-2 text-xs font-mono" style={{ color: '#4A154B' }}>#testimonials</div>
                <div className="px-4 pb-2 text-sm" style={{ color: '#1D1C1D' }}>{quote}</div>
                {inner}
            </div>
        );
    }
    if (channel === 'whatsapp') {
        return (
            <div className={wrapperBase} style={{ ...stagger, background: '#E7FFDB', borderRadius: '12px' }}>
                <div className="px-4 py-3 text-sm" style={{ color: '#111B21' }}>{quote}</div>
                <div className="px-4 pb-1 text-right text-[10px]" style={{ color: '#667781' }}>10:42 AM</div>
                {inner}
            </div>
        );
    }
    if (channel === 'imessage') {
        return (
            <div className={wrapperBase} style={{ ...stagger, background: 'linear-gradient(135deg, #007AFF, #34AADC)', borderRadius: '18px' }}>
                <div className="px-4 py-3 text-sm text-white">{quote}</div>
                <div className="px-4 pb-3">{React.cloneElement(inner, {}, <div className="flex items-center gap-2 mt-3"><Avatar name={name} image_url={image_url} color="#fff" /><div><div className="font-semibold text-sm text-white/90">{name}</div>{(title || company) && <div className="text-xs text-white/70">{[title, company].filter(Boolean).join(' @ ')}</div>}</div></div>)}</div>
            </div>
        );
    }
    if (channel === 'email') {
        return (
            <div className={wrapperBase} style={{ ...stagger, background: '#fff', border: '1px solid #e5e7eb' }}>
                <div className="px-4 py-2 border-b border-gray-100 text-xs" style={{ color: '#6b7280' }}>
                    <div><strong>From:</strong> {name} {company ? `<${name.toLowerCase().replace(/\s/g, '.')}@${company.toLowerCase()}.com>` : ''}</div>
                    <div><strong>Subject:</strong> Re: Feedback</div>
                </div>
                <div className="px-4 py-3 text-sm" style={{ color: '#374151' }}>{quote}</div>
                {inner}
            </div>
        );
    }
    if (channel === 'twitter') {
        return (
            <div className={wrapperBase} style={{ ...stagger, background: '#fff', border: '1px solid #e5e7eb' }}>
                <div className="p-4 flex gap-3">
                    <Avatar name={name} image_url={image_url} color="#1DA1F2" />
                    <div className="flex-1">
                        <div className="flex items-center gap-1">
                            <span className="font-bold text-sm" style={{ color: '#0F1419' }}>{name}</span>
                            <span className="text-xs" style={{ color: '#536471' }}>@{(name || '').toLowerCase().replace(/\s/g, '')}</span>
                        </div>
                        <div className="mt-1 text-sm" style={{ color: '#0F1419' }}>{quote}</div>
                    </div>
                </div>
            </div>
        );
    }
    // linkedin
    return (
        <div className={wrapperBase} style={{ ...stagger, background: '#fff', border: '1px solid #e5e7eb', borderTop: '3px solid #0A66C2' }}>
            <div className="p-4">
                <div className="flex items-center gap-3 mb-2">
                    <Avatar name={name} image_url={image_url} color="#0A66C2" />
                    <div>
                        <div className="font-semibold text-sm" style={{ color: '#000' }}>{name}</div>
                        {(title || company) && <div className="text-xs" style={{ color: '#666' }}>{[title, company].filter(Boolean).join(' at ')}</div>}
                    </div>
                </div>
                <div className="text-sm" style={{ color: '#333' }}>{quote}</div>
            </div>
        </div>
    );
}

export default function TestimonialReceiptsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'masonry';
    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    if (items.length === 0) return null;

    const handleMouseDown = (e) => { setIsDragging(true); setStartX(e.pageX - scrollRef.current.offsetLeft); setScrollLeft(scrollRef.current.scrollLeft); };
    const handleMouseMove = (e) => { if (!isDragging) return; e.preventDefault(); const x = e.pageX - scrollRef.current.offsetLeft; scrollRef.current.scrollLeft = scrollLeft - (x - startX); };
    const handleMouseUp = () => setIsDragging(false);

    return (
        <section ref={ref} className="relative py-16 md:py-24 overflow-hidden" style={{ background: s.dark }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className={`max-w-6xl mx-auto px-4 sm:px-6 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} s={s} primaryColor={primaryColor} secondaryColor={secondaryColor} />

                {variant === 'masonry' && (
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                        {items.map((item, i) => (
                            <div key={i} className="break-inside-avoid">
                                <ReceiptCard item={item} s={s} index={i} visible={visible} />
                                <div className="mt-2 ml-1"><ChannelBadge channel={item.channel} s={s} /></div>
                            </div>
                        ))}
                    </div>
                )}

                {variant === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {items.map((item, i) => (
                            <div key={i}>
                                <ReceiptCard item={item} s={s} index={i} visible={visible} />
                                <div className="mt-2 ml-1"><ChannelBadge channel={item.channel} s={s} /></div>
                            </div>
                        ))}
                    </div>
                )}

                {variant === 'carousel' && (
                    <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4 cursor-grab no-scrollbar" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} style={{ scrollBehavior: isDragging ? 'auto' : 'smooth' }}>
                        {items.map((item, i) => (
                            <div key={i} className="min-w-[300px] md:min-w-[360px] flex-shrink-0">
                                <ReceiptCard item={item} s={s} index={i} visible={visible} />
                                <div className="mt-2 ml-1"><ChannelBadge channel={item.channel} s={s} /></div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
